/**
 * Iter 62.4 Sprint 1 — Founder Ops page
 *
 * One landing page wrapping the 5 zero-risk visibility widgets shipped today:
 *   - Cross-tenant search (A9)
 *   - Investor cohort retention heatmap (B6)
 *   - Integration Hub (D1+D2)
 *   - Onboarding funnel drop-off (A5)
 *   - Top errors + uptime SLA (E3+E5)
 *
 * Mounted under /governance/founder-ops via the SuperAdmin shell.
 */
import { useEffect, useState, useCallback } from 'react';
import { PageHeader } from '../design';
import { governanceAPI } from '../../../lib/governanceApi';
import { useSuperAdminTheme } from '../../../contexts/SuperAdminThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Search, Users, Building2, Briefcase, UserCheck, TrendingDown, AlertTriangle, Activity, Plug, BrainCircuit, Database, RefreshCw } from 'lucide-react';

function pctColor(pct) {
  if (pct >= 80) return 'bg-emerald-500';
  if (pct >= 60) return 'bg-lime-500';
  if (pct >= 40) return 'bg-amber-500';
  if (pct >= 20) return 'bg-orange-500';
  return 'bg-rose-500';
}

// ─────────────────────────────────────────────────────────────
// A9 — Cross-tenant search
// ─────────────────────────────────────────────────────────────
function CrossTenantSearchWidget() {
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    if (!q || q.length < 2) return;
    setLoading(true);
    try {
      const r = await governanceAPI.crossTenantSearch(q, 20);
      setData(r.data);
    } catch (err) {
      setData({ error: err?.response?.data?.detail || 'Search failed' });
    } finally { setLoading(false); }
  };

  return (
    <Card data-testid="founder-search-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Search className="w-4 h-4" /> Cross-tenant search</CardTitle>
        <CardDescription>Find any user / tenant / org / lead by email, name, or ID.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            data-testid="founder-search-input"
            placeholder="email / name / tenant_id / org name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button type="submit" data-testid="founder-search-btn" disabled={loading || q.length < 2}>
            {loading ? '...' : 'Search'}
          </Button>
        </form>
        {data?.error && <div className="text-rose-500 text-sm">{data.error}</div>}
        {data?.counts && (
          <div className="grid grid-cols-4 gap-3 text-center text-xs">
            <Badge variant="outline"><Users className="w-3 h-3 mr-1" />{data.counts.users} users</Badge>
            <Badge variant="outline"><Building2 className="w-3 h-3 mr-1" />{data.counts.tenants} tenants</Badge>
            <Badge variant="outline"><Briefcase className="w-3 h-3 mr-1" />{data.counts.organizations} orgs</Badge>
            <Badge variant="outline"><UserCheck className="w-3 h-3 mr-1" />{data.counts.leads} leads</Badge>
          </div>
        )}
        {data?.results?.users?.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs font-semibold text-muted-foreground">Users</div>
            {data.results.users.map(u => (
              <div key={u.id} className="text-xs flex justify-between border-b py-1">
                <span><b>{u.name || '(no name)'}</b> · {u.email}</span>
                <span className="text-muted-foreground">{(u.tenant_id || '').slice(0, 8)} · {u.role}</span>
              </div>
            ))}
          </div>
        )}
        {data?.results?.tenants?.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs font-semibold text-muted-foreground">Tenants</div>
            {data.results.tenants.map(t => (
              <div key={t.id} className="text-xs flex justify-between border-b py-1">
                <span><b>{t.name}</b> · {t.industry || '—'}</span>
                <span className="text-muted-foreground">{t.tenant_type} · seeded={String(t.brain_data_seeded || false)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// B6 — Cohort retention heatmap
// ─────────────────────────────────────────────────────────────
function CohortRetentionWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    governanceAPI.getCohortRetention(6)
      .then(r => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card data-testid="cohort-retention-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Activity className="w-4 h-4" /> Cohort retention (6 months)</CardTitle>
        <CardDescription>% of signup-cohort still active per subsequent month. Investor-grade NRR proxy.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading && <Skeleton className="h-40 w-full" />}
        {data?.error && <div className="text-rose-500 text-sm">Failed to load</div>}
        {data?.matrix && (
          <div className="overflow-x-auto">
            <table className="text-xs w-full">
              <thead>
                <tr>
                  <th className="text-left p-1">Cohort</th>
                  <th className="text-left p-1">Size</th>
                  {Array.from({ length: 7 }, (_, i) => <th key={i} className="text-center p-1">M{i}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.matrix.map(row => (
                  <tr key={row.cohort}>
                    <td className="p-1 font-medium">{row.cohort}</td>
                    <td className="p-1 text-muted-foreground">{row.size}</td>
                    {row.retention.slice(0, 7).map(cell => (
                      <td key={cell.month_offset} className="p-0.5">
                        <div
                          className={`${pctColor(cell.pct)} text-white rounded text-center py-1`}
                          title={`${cell.active} active / ${row.size} (${cell.pct}%)`}
                        >
                          {cell.pct}%
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// D1+D2 — Integration Hub
// ─────────────────────────────────────────────────────────────
function IntegrationHubWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    governanceAPI.getIntegrationHub()
      .then(r => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card data-testid="integration-hub-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Plug className="w-4 h-4" /> Integration Hub</CardTitle>
        <CardDescription>Per-provider adoption + webhook health (24h) + extension heartbeats.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <Skeleton className="h-32 w-full" />}
        {data?.error && <div className="text-rose-500 text-sm">Failed to load</div>}
        {data?.totals && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="border rounded p-2"><div className="text-muted-foreground">LinkedIn ext (24h)</div><div className="text-lg font-bold">{data.totals.extensions_active_24h?.linkedin || 0}</div></div>
            <div className="border rounded p-2"><div className="text-muted-foreground">WhatsApp ext (24h)</div><div className="text-lg font-bold">{data.totals.extensions_active_24h?.whatsapp || 0}</div></div>
          </div>
        )}
        {data?.providers && (
          <table className="text-xs w-full">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1">Provider</th><th>Configured</th><th>Active</th><th>Tests OK</th><th>WH 24h</th>
              </tr>
            </thead>
            <tbody>
              {data.providers.map(p => (
                <tr key={p.name} className="border-t">
                  <td className="py-1 font-medium capitalize">{p.name}</td>
                  <td>{p.adoption.configured_tenants || 0}</td>
                  <td>{p.adoption.active_tenants || 0}</td>
                  <td>{p.adoption.test_passed_tenants || 0}</td>
                  <td>
                    {p.webhook_24h.total > 0
                      ? <span className={p.webhook_24h.fail === 0 ? 'text-emerald-500' : 'text-rose-500'}>{p.webhook_24h.success}/{p.webhook_24h.total}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// A5 — Onboarding funnel
// ─────────────────────────────────────────────────────────────
function OnboardingFunnelWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    governanceAPI.getOnboardingFunnel(30)
      .then(r => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card data-testid="onboarding-funnel-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><TrendingDown className="w-4 h-4" /> Onboarding funnel (30 days)</CardTitle>
        <CardDescription>
          Drop-off across 7 activation stages.
          {data?.biggest_drop_off && (
            <span className="ml-2 text-amber-500">
              Biggest gap: {data.biggest_drop_off.from} → {data.biggest_drop_off.to} (-{data.biggest_drop_off.pct_lost}%)
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && <Skeleton className="h-40 w-full" />}
        {data?.stages && (
          <div className="space-y-2">
            {data.stages.map(s => (
              <div key={s.key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium">{s.label}</span>
                  <span className="text-muted-foreground">{s.count} · {s.pct}%</span>
                </div>
                <div className="h-2 bg-muted rounded overflow-hidden">
                  <div className={`${pctColor(s.pct)} h-2 rounded`} style={{ width: `${Math.min(100, s.pct)}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// E3+E5 — Top errors + uptime SLA
// ─────────────────────────────────────────────────────────────
function ObservabilityWidget() {
  const [err, setErr] = useState(null);
  const [up, setUp] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        governanceAPI.getTopErrors(24, 10),
        governanceAPI.getUptimeSla(30),
      ]);
      setErr(a.data);
      setUp(b.data);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <Card data-testid="observability-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Observability</CardTitle>
        <CardDescription>Top errors (24h) + rolling uptime SLA (30 days).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <Skeleton className="h-32 w-full" />}
        {up && (
          <div className="flex items-center justify-between border rounded p-2 text-xs">
            <span>Uptime (30d)</span>
            <span className="font-bold text-lg">
              {up.uptime_pct == null ? '—' : `${up.uptime_pct}%`}
            </span>
            <span className={up.sla_status === 'above_target' ? 'text-emerald-500' : up.sla_status === 'below_target' ? 'text-rose-500' : 'text-muted-foreground'}>
              SLA {up.sla_target}% · {up.sla_status}
            </span>
          </div>
        )}
        {up?.note && <div className="text-xs text-muted-foreground italic">{up.note}</div>}
        {err && (
          <div>
            <div className="text-xs font-semibold mb-1">Top errors ({err.total_errors} total in 24h)</div>
            {err.top_errors.length === 0 ? (
              <div className="text-xs text-emerald-500">No errors in window 🎉</div>
            ) : (
              <ul className="text-xs space-y-0.5">
                {err.top_errors.map(e => (
                  <li key={e.key} className="flex justify-between">
                    <span className="font-mono text-rose-500">{e.key}</span>
                    <span className="text-muted-foreground">{e.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const POLL_BR_MS = 30000;

function StatTile({ label, value, tone = 'default', testid }) {
  const toneClass = {
    default: 'text-foreground',
    good: 'text-emerald-500',
    warn: 'text-amber-500',
    bad: 'text-rose-500',
  }[tone] || 'text-foreground';
  return (
    <div className="border rounded p-2 text-center" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold ${toneClass}`}>{value}</div>
    </div>
  );
}

function BrainReliabilityWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await governanceAPI.getBrainReliability();
      setData(r.data);
    } catch (e) {
      setData({ error: true });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_BR_MS);
    return () => clearInterval(t);
  }, [refresh]);

  const overall = data?.overall;
  const badgeTone = overall === 'healthy' ? 'bg-emerald-500/15 text-emerald-500'
    : overall === 'warning' ? 'bg-amber-500/15 text-amber-500'
    : overall === 'critical' ? 'bg-rose-500/15 text-rose-500' : 'bg-muted text-muted-foreground';
  const jobs = data?.jobs || {};
  const dbh = data?.db_health || {};
  const conns = dbh.connections || {};

  return (
    <Card data-testid="brain-reliability-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BrainCircuit className="w-4 h-4" /> Brain Reliability & DB Health
            </CardTitle>
            <CardDescription>Durable-queue health, dead-letters, event backlog and live MongoDB status.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {overall && (
              <span className={`text-xs font-semibold px-2 py-1 rounded ${badgeTone}`} data-testid="brain-reliability-overall">
                {overall.toUpperCase()}
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={refresh} data-testid="brain-reliability-refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && <Skeleton className="h-32 w-full" />}
        {data?.error && <div className="text-rose-500 text-sm">Failed to load brain reliability.</div>}
        {data && !data.error && (
          <>
            {/* brain_jobs queue breakdown */}
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1.5">Durable Job Queue (brain_jobs)</div>
              <div className="grid grid-cols-5 gap-2">
                <StatTile label="Queued" value={jobs.queued ?? 0} testid="br-jobs-queued" />
                <StatTile label="Processing" value={jobs.processing ?? 0} testid="br-jobs-processing" />
                <StatTile label="Done" value={jobs.done ?? 0} tone="good" testid="br-jobs-done" />
                <StatTile label="Failed" value={jobs.failed ?? 0} tone={(jobs.failed ?? 0) > 0 ? 'warn' : 'default'} testid="br-jobs-failed" />
                <StatTile label="Dead" value={jobs.dead ?? 0} tone={(jobs.dead ?? 0) > 0 ? 'bad' : 'good'} testid="br-jobs-dead" />
              </div>
              <div className="text-[11px] text-muted-foreground mt-1.5">
                Oldest queued: <span className={`font-semibold ${(data.oldest_queued_minutes ?? 0) >= 15 ? 'text-amber-500' : 'text-foreground'}`}>{data.oldest_queued_minutes ?? 0} min</span>
              </div>
            </div>

            {/* events backlog */}
            <div className="grid grid-cols-2 gap-2">
              <StatTile label="Events failed (24h)" value={data.events?.failed_24h ?? 0} tone={(data.events?.failed_24h ?? 0) > 0 ? 'warn' : 'default'} testid="br-events-failed" />
              <StatTile label="Event retry backlog" value={data.events?.retry_backlog ?? 0} testid="br-events-retry" />
            </div>

            {/* DB health */}
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Database className="w-3.5 h-3.5" /> MongoDB Health
              </div>
              <div className="grid grid-cols-4 gap-2">
                <StatTile label="Status" value={dbh.status === 'healthy' ? 'OK' : (dbh.status || '—')} tone={dbh.status === 'healthy' ? 'good' : 'bad'} testid="br-db-status" />
                <StatTile label="Ping" value={dbh.ping_ms != null ? `${dbh.ping_ms}ms` : '—'} tone={dbh.ping_ms != null && dbh.ping_ms < 50 ? 'good' : dbh.ping_ms != null && dbh.ping_ms < 200 ? 'warn' : 'default'} testid="br-db-ping" />
                <StatTile label="Conns (cur)" value={conns.current ?? '—'} testid="br-db-conns-current" />
                <StatTile label="Conns (avail)" value={conns.available ?? '—'} testid="br-db-conns-available" />
              </div>
            </div>

            {/* dead-letters */}
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1.5">
                Recent Dead-Letters (24h): {data.dead_letter_count ?? 0}
              </div>
              {(data.dead_letters || []).length === 0 ? (
                <div className="text-xs text-emerald-500">No dead-lettered jobs in the last 24h 🎉</div>
              ) : (
                <ul className="text-xs space-y-1 max-h-40 overflow-y-auto" data-testid="br-dead-letters">
                  {data.dead_letters.map((d) => (
                    <li key={d.id} className="border-b pb-1">
                      <div className="flex justify-between">
                        <span className="font-mono text-rose-500">{d.job_type}</span>
                        <span className="text-muted-foreground">{d.attempts} attempts</span>
                      </div>
                      <div className="text-muted-foreground truncate" title={d.last_error}>{d.last_error}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function FounderOps() {
  const { theme } = useSuperAdminTheme();
  return (
    <div className="space-y-6" data-testid="founder-ops-page">
      <PageHeader
        title="Founder Ops"
        subtitle="Read-only visibility widgets + Brain reliability & DB health."
      />
      <BrainReliabilityWidget />
      <CrossTenantSearchWidget />
      <div className="grid gap-4 lg:grid-cols-2">
        <CohortRetentionWidget />
        <IntegrationHubWidget />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <OnboardingFunnelWidget />
        <ObservabilityWidget />
      </div>
    </div>
  );
}
